<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

type Variant = 'primary' | 'secondary' | 'navy' | 'blue' | 'outline' | 'ghost'
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    to?: RouteLocationRaw
    href?: string
    target?: string
    rel?: string
    type?: 'button' | 'submit' | 'reset'
    variant?: Variant
    size?: Size
    loading?: boolean
    disabled?: boolean
  }>(),
  {
    type: 'button',
    variant: 'primary',
    size: 'md',
    loading: false,
    disabled: false,
  },
)

const isDisabled = computed(() => props.disabled || props.loading)

const variantClass = computed(() => {
  switch (props.variant) {
    case 'secondary':
      return 'lp-btn--secondary'
    case 'navy':
      return 'lp-btn--navy'
    case 'blue':
      return 'lp-btn--blue'
    case 'outline':
      return 'lp-btn--outline'
    case 'ghost':
      return 'lp-btn--ghost'
    case 'primary':
    default:
      return 'lp-btn--primary'
  }
})

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'lp-btn--sm'
    case 'lg':
      return 'lp-btn--lg'
    case 'md':
    default:
      return ''
  }
})

function onClick(event: MouseEvent) {
  if (!isDisabled.value) return
  event.preventDefault()
  event.stopPropagation()
}
</script>

<template>
  <NuxtLink
    v-if="to"
    :to="to"
    class="lp-btn app-btn"
    :class="[variantClass, sizeClass]"
    :aria-disabled="isDisabled ? 'true' : undefined"
    :tabindex="isDisabled ? -1 : undefined"
    @click="onClick"
  >
    <span v-if="loading" class="app-btn__spinner" aria-hidden="true" />
    <slot />
  </NuxtLink>

  <a
    v-else-if="href"
    :href="href"
    :target="target"
    :rel="rel"
    class="lp-btn app-btn"
    :class="[variantClass, sizeClass]"
    :aria-disabled="isDisabled ? 'true' : undefined"
    :tabindex="isDisabled ? -1 : undefined"
    @click="onClick"
  >
    <span v-if="loading" class="app-btn__spinner" aria-hidden="true" />
    <slot />
  </a>

  <button
    v-else
    :type="type"
    class="lp-btn app-btn"
    :class="[variantClass, sizeClass]"
    :disabled="isDisabled"
  >
    <span v-if="loading" class="app-btn__spinner" aria-hidden="true" />
    <slot />
  </button>
</template>
